import { useFontRender } from "@/components/FontRender";
import FormatedText from "@/components/FormatedText";
import {
  StyledIcon,
  StyledLink,
} from "@/components/microsite/components/LinkComponents";
import {
  autoLinksPrefix,
  autoLinksValidations,
} from "@/components/microsite/links";
import ModalComponent, { ModalHeader, useModal } from "@/components/Modal";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { useAPI } from "@/lib/api";
import { cn } from "@/lib/utils";
import { IMicrositeComponent, MComponentLinkProps } from "@/types/microsite";
import shortUrl from "@/vars/shortUrl";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import dynamic from "next/dynamic";
import { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import slugify from "slugify";
import { useProtected } from "./ProtectedModal";
import { TextComponentStyle } from "./Text";

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

const LinkComponent = {
  kind: "LINK",
  Render: ({
    microsite,
    isBuilder,
    id,
    isFocus,
    data: {
      href: _href,
      value: _value,
      linkType: _linkType,
      valueSecondary: _valueSecondary,
      content,
      icon,
      textFont,
      buttonEffect,
      buttonColor,
      textPosition,
      needPassword: _needPassword,
      passwordMessage,
      keepInPage,
    },
  }) => {
    const api = useAPI();
    const withIcon = !!icon;
    const linkType = _linkType || "link";
    const [needPassword, setNeedPassword] = useState(_needPassword);
    const contactModal = useModal();
    const [{ href, value, valueSecondary }, setData] = useState({
      href: _href,
      value: _value,
      valueSecondary: _valueSecondary,
    });
    const { focusTo } = useComponent();

    const { fontStyle, RenderFontStyle } = useFontRender({
      textFont,
    });

    const protectedProp = useProtected({
      micrositeId: microsite?.linkId,
      componentId: id,
      message: passwordMessage,
      isBuilder,
      onSuccess: (data) => {
        setData(data);
        track();
        window.open(getLink(data), "_blank", "noopener noreferrer");
        setNeedPassword(false);
      },
    });

    const buttonType = microsite?.style?.buttonType || "elevate";
    const hasButtonShadow = !!microsite?.style?.buttonShadowColor;
    const buttonBorderColor = microsite?.style?.buttonBorderColor;

    const handleLink = (e) => {
      // if (linkType === "mail") {
      //   e.preventDefault();
      //   contactModal.open();
      //   return;
      // }

      if (isBuilder) {
        e.preventDefault();
        focusTo(id as string);
        return;
      }

      if (needPassword) {
        e.preventDefault();
        protectedProp.onRequest();
        return;
      }

      // if (linkType === "phone") {
      //   e.preventDefault();
      //   contactModal.open();
      //   return;
      // }

      // if (microsite?.extras?.affiliate) {
      //   setTimeout(() => {
      //     window.open(microsite?.extras?.affiliate, "_blank");
      //   }, 1);
      // }

      track();
    };

    const track = () => {
      const post = {
        component_id: id,
        action_type: "button",
      };
      api.post("/track/microsite", post).then(() => {
        //...
      });
    };

    useEffect(() => {
      setData({ href: _href, value: _value, valueSecondary: _valueSecondary });
    }, [_href, _value, _valueSecondary]);

    const getLink = ({ href, value, linkType, valueSecondary }) => {
      if (linkType !== "link") {
        const validate = autoLinksValidations[linkType];
        if (validate && validate(value)) {
          let text = autoLinksPrefix[linkType] + value;

          if (linkType === "whatsapp") {
            if (valueSecondary) {
              text += "?text=" + encodeURIComponent(valueSecondary);
            }
          }

          return text;
        }
      }
      return href;
    };

    const realLink = useMemo(() => {
      return getLink({ href, value, linkType, valueSecondary });
    }, [href, value, linkType, valueSecondary]);

    const isKeepInPage = useMemo(() => {
      return (
        linkType === "link" && href && href.startsWith(shortUrl) && keepInPage
      );
    }, [linkType, href]);

    useEffect(() => {
      if (isFocus && needPassword) {
        setTimeout(() => {
          handleLink({
            preventDefault: () => {
              //...
            },
          });
        }, 1000);
      }
    }, [isFocus, needPassword]);

    if (content?.length > 0) {
      return (
        <Fragment>
          {RenderFontStyle}
          <LinkComponentStyle
            id={`mcomp-${id}`}
            className="z-10 relative m-2 w-full"
          >
            <StyledLink
              buttonColor={buttonColor}
              buttonEffect={buttonEffect}
              buttonType={buttonType}
              hasButtonShadow={hasButtonShadow}
              buttonBorderColor={buttonBorderColor}
              isButton={needPassword}
              href={realLink}
              target={isKeepInPage ? undefined : "_blank"}
              rel="noopener noreferrer"
              onClick={handleLink}
            >
              {withIcon ? (
                <StyledIcon buttonType={buttonType}>
                  <Suspense>
                    <RenderIcon icon={icon} iconSize="var(--icon-width)" />
                  </Suspense>
                </StyledIcon>
              ) : (
                <div
                  className={cn(textPosition === "left" ? "" : "shrink-0")}
                  style={{
                    width: "var(--button-height)",
                    height: "var(--button-height)",
                    margin: "var(--button-margin)",
                  }}
                />
              )}
              <div className={cn("flex items-center px-2 py-2 w-full")}>
                <TextComponentStyle style={fontStyle} position={textPosition}>
                  <FormatedText value={content} singleLine />
                </TextComponentStyle>
              </div>
              {needPassword ? (
                <StyledIcon buttonType={buttonType}>
                  <LockClosedIcon className="w-5" />
                </StyledIcon>
              ) : (
                <div
                  className={textPosition === "right" ? "" : "shrink-0"}
                  style={{
                    width: "var(--button-height)",
                    height: "var(--button-height)",
                    margin: "var(--button-margin)",
                  }}
                />
              )}
            </StyledLink>
          </LinkComponentStyle>
          <ContactModal
            modalProp={contactModal}
            value={value}
            linkType={linkType}
            microsite={microsite}
          />
        </Fragment>
      );
    }

    return <Fragment />;
  },
} as IMicrositeComponent<MComponentLinkProps>;

export const LinkComponentStyle = (props) => {
  return (
    <div
      {...props}
      className={cn("w-full m-2 relative z-10", props.className)}
    />
  );
};

const ContactModal = ({ modalProp, microsite, value, linkType }) => {
  const downloadVcard = () => {
    const data = ["BEGIN:VCARD", "VERSION:3.0", "FN:" + microsite.title];

    if (linkType === "phone") {
      data.push("TEL:" + value);
    }

    if (linkType === "mail") {
      data.push("EMAIL;CHARSET=UTF-8;type=HOME,INTERNET:" + value);
    }

    data.push("END:VCARD");

    const vCardBlob = new Blob([data.join("\n")], { type: "text/vcard" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(vCardBlob);
    link.download = `${slugify(microsite.title, {
      lower: true,
      trim: true,
      strict: true,
    })}.vcf`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <ModalComponent {...modalProp}>
      <ModalHeader {...modalProp}>Contact</ModalHeader>
      <button
        className="hover:bg-slate-50 mb-3 p-3 border rounded-md w-full font-medium"
        onClick={downloadVcard}
      >
        💾 Save Contact
      </button>

      {linkType === "phone" && (
        <a
          href={`tel:${value}`}
          className="block hover:bg-slate-50 p-3 border rounded-md w-full font-medium text-center"
        >
          ☎️ Call Now
        </a>
      )}

      {linkType === "mail" && (
        <a
          href={`mailto:${value}`}
          className="block hover:bg-slate-50 p-3 border rounded-md w-full font-medium text-center"
        >
          ✉️ Email
        </a>
      )}
    </ModalComponent>
  );
};

export default LinkComponent;
